import {
	ReactElement,
	useCallback,
	useEffect,
	useState,
} from "react";
import {
	Dialog,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError,
	Form
} from "client/Components";
import {
	localize
} from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	AfasOneTimePasswordResponseKind,
	AfasOneTimePasswordResponseModel,
	RequestNewTokenModel,
	requestNewToken,
	requestOneTimePassword
} from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type OtpDialogProps = {
	onClose(): void;
	connectionId: number;
	onAfterSave: () => void;
};

export const OtpDialog = (props: OtpDialogProps): ReactElement => {
	const {
		onClose,
		connectionId,
		onAfterSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<RequestNewTokenModel>({
		connectionId,
		oneTimePassword: ""
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<RequestNewTokenModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			try {
				setIsBusy(true);
				await requestNewToken(formData, globalCancellation);
				setIsBusy(false);

				onAfterSave();
				alertManager.showSuccess(localize("_SuccessfullyActivatedAfas"));
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_InvalidCodePleaseTryAgain"));
				}
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			onAfterSave,
			onClose,
			globalCancellation
		]
	);

	useEffect(
		() => {
			async function requestNewToken(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const result: AfasOneTimePasswordResponseModel = await requestOneTimePassword(
						connectionId,
						signal
					);
					setIsBusy(false);
					const { isAdmin } = userConfiguration();

					if (result.kind === AfasOneTimePasswordResponseKind.RequireEmailAddress) {
						alertManager.showError(
							localize(isAdmin
								? "_AfasEmailRequiredToActivateMessageForAdmin"
								: "_AfasEmailRequiredToActivateMessage"
							)
						);
					}
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			requestNewToken(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			connectionId,
			onClose
		]
	);

	return (
		<Dialog
			trackingId="afas-otp-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_EnterActivationCode")}
			/>

			<Dialog.Body>
				<p>{localize("_AfasActivationCodeIsSentToEmailHint")}</p>
				<Form
					onSubmit={submitHandler}
				>
					<FormItem
						label={localize("_ActivationCode")}
					>
						<TextInput
							{...formHandler.for("oneTimePassword").attachToTextInput()}
						/>
						<ValidationError {...formHandler.for("oneTimePassword").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Activate")}
						onClick={submitHandler}
						trackingId="activate"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

OtpDialog.displayName = "OtpDialog";

const validationConfiguration: IValidationConfiguration<RequestNewTokenModel> =
	createValidationConfiguration<RequestNewTokenModel>();

validationConfiguration.forScalar("oneTimePassword")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);